import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Grid,
  Container,
  styled,
  TextField,
  FormControl,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import config from '../config';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import InfoIcon from '@mui/icons-material/Info';
import axiosInstance from '../utils/axiosInstance';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
          borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  marginTop: theme.spacing(4),
}));

const PriceText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
}));

const OrderConfirm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    phone: false
  });

  if (!state?.template) {
    navigate('/');
    return null;
  }

  const { _id: templateId, name, thumbnail, thumbnails, pricing } = state.template;
  const { uuid } = state;
  const { amount, currency } = pricing;

  // Priority: Use first image from thumbnails array if available, otherwise use thumbnail
  const getThumbnailUrl = () => {
    if (thumbnails && Array.isArray(thumbnails) && thumbnails.length > 0) {
      return `${config.serverUrl}/public/biodata/thumbnails/${thumbnails[0]}`;
    } else if (thumbnail) {
      return `${config.serverUrl}/public/biodata/thumbnails/${thumbnail}`;
    }
    return null;
  };

  const thumbnailUrl = getThumbnailUrl();

  const validateForm = () => {
    const newErrors = {
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
      phone: !/^[0-9]{10,15}$/.test(phone)
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleProceedToPayment = async () => {
    if (!validateForm()) {
      toast.error("Please enter valid email and phone number");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
        `${config.apiBaseUrl}/payment/initiate/${uuid}`,
        {
          amount,
          templateId,
          email,
          phoneNumber: phone
        }
      );

      if (response.data?.redirectUrl) window.location.href = response.data.redirectUrl;
      else if (response.data?.message) toast.error(response.data?.message);

    } catch (error) {
      toast.error(error.response?.data?.message || "Payment initiation failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <ToastContainer />
      <Box textAlign="center" mb={4}>
        <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
        <Typography variant="h4" component="h1" gutterBottom>
          Order Summary
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Review your template and enter your details
        </Typography>
      </Box>

      <StyledPaper elevation={3}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={thumbnailUrl}
              alt={name}
              sx={{
                width: '100%',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                aspectRatio: '1/1.414',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/300x420?text=Template+Preview';
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" height="100%">
              <Typography variant="h5" component="h2" gutterBottom>
                {name}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box flexGrow={1}>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <TextField
                    label="Email Address"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={errors.email}
                    helperText={errors.email ? "Please enter a valid email" : ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                    required
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <TextField
                    label="Phone Number"
                    variant="outlined"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    error={errors.phone}
                    helperText={errors.phone ? "Please enter 10 digit phone number" : ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                    required
                  />
                </FormControl>

                <Alert 
                  icon={<InfoIcon />} 
                  severity="info" 
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2">
                    After successful payment, your downloaded template will be sent to your email address and mobile number. The email will include details and links to modify your template and update your biodata.
                  </Typography>
                </Alert>

                <Box display="flex" alignItems="center" mt={3} mb={4}>
                  <Typography variant="h6" mr={1}>
                    Total:
                  </Typography>
                  <PriceText variant="h5">
                    {currency} {amount.toFixed(2)}
                  </PriceText>
                </Box>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleProceedToPayment}
                sx={{ py: 1.5, mt: 'auto' }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Proceed to Payment"
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </StyledPaper>

      <Box mt={4} textAlign="center">
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back to Templates
        </Button>
        <Button
          variant="text"
          onClick={() => navigate('/')}
        >
          Return Home
        </Button>
      </Box>
    </Container>
  );
};

export default OrderConfirm;